import { createQueryKeys, inferQueryKeys } from '@lukemorales/query-key-factory';
import { AxiosResponse } from 'axios';

import { configQueryKeys } from '@apis/configApi';
import {
  $httpCommonApi,
  $httpRootApi,
  queryClient,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@core/http-client';
import { ConfigCore } from '@shared/models/config';
import { UserProfile } from '@shared/models/user';
import { getQueriesAsSearch } from '@shared/utils/common';

import {
  UsersListPayload,
  UsersListResponse,
  UsersUpdateProfileImagePayload,
  UsersUpdateProfileImageResponse,
  UserUpdateNotificationsPayload,
  UserUpdatePasswordPayload,
  UserUpdatePayload,
} from './usersApi.types';

// ---- Queries ----

export const usersQueryKeys = createQueryKeys('users', {
  all: null,
  // TODO: Move list to agency api
  list: (payload: UsersListPayload) => ({
    queryKey: [payload.query],
    queryFn: async ({ signal }) =>
      (await $httpRootApi.get<UsersListResponse>(`/users${getQueriesAsSearch(payload.query)}`, { signal })).data,
  }),
});

export type UsersQueryKeys = inferQueryKeys<typeof usersQueryKeys>;

// TODO: Move list to agency api
export const useUsersListQuery = (payload: UsersListPayload, options?: UseQueryOptions<UsersListResponse>) =>
  useQuery<UsersQueryKeys['list']['queryKey'], UsersListResponse>({
    ...usersQueryKeys.list(payload),
    keepPreviousData: true,
    ...options,
  });

// ---- Mutations ----

export const userMutations = {
  update: async (payload: UserUpdatePayload) => {
    const response = await $httpCommonApi.put<UserUpdatePayload['body'], AxiosResponse<UserProfile>>(
      `users/${payload.id}`,
      payload.body
    );

    updateUserCache(response.data);

    return response;
  },
};

export const useUserUpdateMutation = (opts?: UseMutationOptions<UserUpdatePayload, UserProfile>) =>
  useMutation({
    ...opts,
    mutationFn: async (data) => (await userMutations.update(data)).data,
  });

// TODO: Move notifications to agency api
export const useUserUpdateNotificationsMutation = (opts?: UseMutationOptions<UserUpdateNotificationsPayload, any>) =>
  useMutation({
    ...opts,
    mutationFn: async (payload: UserUpdateNotificationsPayload) => {
      const response = await $httpRootApi.put<UserUpdateNotificationsPayload['body'], AxiosResponse<any>>(
        `users/${payload.id}/update/notifications`,
        payload.body
      );

      // Update user
      return updateUserCache(response.data);
    },
  });

export const useUserUpdatePasswordMutation = (opts?: UseMutationOptions<UserUpdatePasswordPayload, any>) =>
  useMutation({
    ...opts,
    mutationFn: async (payload: UserUpdatePasswordPayload) =>
      (
        await $httpCommonApi.put<UserUpdatePasswordPayload['body'], AxiosResponse<any>>(
          `users/${payload.id}/password`,
          payload.body
        )
      ).data,
  });

export const useUsersUpdateProfileImageMutation = (
  opts?: UseMutationOptions<UsersUpdateProfileImagePayload, UsersUpdateProfileImageResponse>
) =>
  useMutation({
    ...opts,
    mutationFn: async (payload: UsersUpdateProfileImagePayload) =>
      (
        await $httpCommonApi.post<
          UsersUpdateProfileImagePayload['body'],
          AxiosResponse<UsersUpdateProfileImageResponse>
        >(`users/${payload.id}/profile_pic`, payload.body)
      ).data,
  });

// ---- Utils ----

export const updateUserCache = (userData: Partial<ConfigCore['user']>) => {
  queryClient.setQueryData<ConfigCore>(configQueryKeys.showAgency().queryKey, (config) => {
    if (config) {
      return {
        ...config,
        user: {
          ...config.user,
          ...userData,
        },
      };
    }

    return config;
  });
};
